import {JobTaskDetailsFragment} from "@platform/components/jobs/job-task-details/job-task-details.generated"

type TaskEvent = JobTaskDetailsFragment["taskEvents"][number]

// Reasons the resource cascade advanced a task, spelled out — the enum values are terse. Mirrors the
// wording in the job run timeline so the two views read the same.
const ADVANCE_REASON_CLAUSE: Record<string, string> = {
    NoCapacity: "the cloud had no capacity for that group",
    PreemptionBlacklist: "that group's preemption circuit breaker was open",
    PreviousFailure: "this task had already failed on that group",
    Misconfigured: "that group is misconfigured (its MIG or template does not exist)",
}

/** How an event reads on the timeline — drives its marker colour and label accent. */
export type TaskEventCategory = "neutral" | "active" | "success" | "warning" | "error" | "action"

// Event types grouped by what they mean for the reader: routine lifecycle steps incl. the coordinator
// bring-up phases (neutral), the running state (active), the single terminal success, transient placement
// problems where the task has NOT failed (warning), hard failures (error), and staff actions. Any type not
// listed here falls back to neutral, so a new enum value degrades gracefully rather than misleading.
const CATEGORY_BY_EVENT: Record<string, TaskEventCategory> = {
    // The bring-up phases (Queued, Activated, Designated, Provisioned) stay neutral: only when the task
    // actually runs does it go blue. Keeping them grey stops a blue "Activated" from making the greys that
    // follow it — Provisioned, or an out-of-order legacy "Queued" — read as a step back.
    Running: "active",
    Completed: "success",
    Preempted: "warning",
    TimedOut: "warning",
    Skipped: "warning",
    CascadeAdvanced: "warning",
    // A stockout (no capacity) is a hard resource failure — red, matching the cascade view — even though a
    // cascade may then fall through to another group. Misconfigured (MIG/template missing) is the same.
    Stockout: "error",
    Misconfigured: "error",
    Failed: "error",
    Fatal: "error",
    Cancelled: "error",
    Repinned: "action",
}

/** A compact, scannable piece of structured context shown as a pill under an event. */
export interface TaskEventChip {
    icon: string
    text: string
}

/** Everything the template needs to render one event, split by how each part should be presented. */
export interface TaskEventView {
    label: string
    category: TaskEventCategory
    /** Free-text message (may be a long cloud error) — shown in a call-out block. */
    message: string | null
    /** The instance identifier (+ machine/zone spec) — shown on its own monospace line. */
    instance: string | null
    /** Short structured facts (resource group, duration) — shown as pills. */
    chips: TaskEventChip[]
    /** Secondary, sentence-like context (why the cascade moved, who acted) — shown muted. */
    notes: string[]
}

// Display-name overrides where the raw enum name reads wrong. "Active" → "Activated" matches the
// past-tense lifecycle wording (Created, Queued, Designated, Provisioned).
const EVENT_LABEL_OVERRIDE: Record<string, string> = {
    Active: "Activated",
}

/** A human-readable label for an event type — split PascalCase into words, with a few overrides. */
export function eventLabel(eventType: string): string {
    return EVENT_LABEL_OVERRIDE[eventType] ?? eventType.replace(/([a-z])([A-Z])/g, "$1 $2")
}

/** A duration in seconds as minutes/seconds, e.g. 45 → "45s", 510 → "8m 30s", 480 → "8m". */
export function formatDurationSeconds(seconds: number): string {
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    const rest = seconds % 60
    return rest === 0 ? `${minutes}m` : `${minutes}m ${rest}s`
}

function eventCategory(eventType: string): TaskEventCategory {
    return CATEGORY_BY_EVENT[eventType] ?? "neutral"
}

/**
 * Turn a raw task event into its presentation model. Splits the flat list of details the old view showed
 * as identical grey lines into distinct kinds — chips, a message block, an instance line, muted notes —
 * so the eye can tell a capacity error apart from a resource-group tag at a glance. Empty fields are
 * dropped, so an event carries only what is relevant to it.
 */
export function eventView(event: TaskEvent): TaskEventView {
    const chips: TaskEventChip[] = []
    if (event.resourceGroupId) chips.push({icon: "far fa-layer-group", text: event.resourceGroupId})
    if (event.durationSeconds != null) chips.push({icon: "far fa-clock", text: `~${formatDurationSeconds(event.durationSeconds)}`})

    let instance: string | null = null
    if (event.instanceName) {
        const spec = [event.machineType, event.zone].filter(Boolean).join(", ")
        instance = spec ? `${event.instanceName} (${spec})` : event.instanceName
    }

    const notes: string[] = []
    if (event.advanceReason) notes.push(`advanced because ${ADVANCE_REASON_CLAUSE[event.advanceReason] ?? event.advanceReason}`)
    if (event.performedByName) notes.push(`by ${event.performedByName}`)

    return {
        label: eventLabel(event.eventType),
        category: eventCategory(event.eventType),
        message: event.message ?? null,
        instance,
        chips,
        notes,
    }
}
